import { Controller } from "@hotwired/stimulus";

/**
 * Event Request Facility Controller
 *
 * Handles the facility step (Step 3) of the event request wizard:
 * - "Same for all days" checkbox toggles for venue, equipment, and F&B
 * - Syncing primary selections to per-day sections
 * - Showing/hiding table columns based on primary selection
 * - Enabling/disabling per-day sections based on checkbox state
 */
export default class extends Controller {
  static targets = [
    // Venue targets
    "venueSameForAll",
    "primaryVenue",
    "primaryLayout",
    "perDayVenuesBox",
    "dayVenue",
    "dayLayout",

    // Equipment targets
    "equipmentSameForAll",
    "primaryEquipmentCheckbox",
    "primaryEquipmentQuantity",
    "perDayEquipmentBox",
    "equipmentColumn",
    "equipmentCell",

    // F&B targets
    "fbSameForAll",
    "primaryFbCheckbox",
    "primaryFbHeadcount",
    "perDayFbBox",
    "fbColumn",
    "fbCell",

    // GR-3294 — running estimate for the step
    "estimateTotal",
    "estimateNote",
  ];

  // GR-3128 — per-venue layout options: { venueId: { options: [[label, value]], default } }
  // GR-3294 — estimateDays is the request's day count; a booking that applies
  // to every day bills once per day, which is what the invoice generator does.
  static values = {
    layoutOptions: Object,
    estimateDays: Number,
    estimateCurrency: String,
  };

  connect() {
    // GR-3128 QA bounce — make every layout select match its venue select
    // before any state syncing copies values around.
    this.syncLayoutSelects();
    // Initialize state based on checkbox values on page load
    this.syncVenueState();
    this.syncEquipmentState();
    this.syncFbState();
    // GR-1967 — reveal T&Cs for whatever is already selected.
    this.syncTerms();
    // GR-1970 — reveal the selected venue's photo / floor plan / location.
    this.syncMedia();
    // GR-3294 — seed the running estimate from whatever is already selected.
    this.recalculateEstimate();
  }

  // The set of currently-selected facility ids for this step: the venue
  // <select> value plus every checked selection checkbox (equipment / F&B /
  // service `*_ids[]`). The T&C acceptance toggle is named
  // `accepted_terms_facility_ids[]`, which also ends in `_ids[]`, so it must
  // be excluded — otherwise accepting the terms would keep the id "selected"
  // and the block would stay visible after the facility is deselected
  // (GR-1967).
  selectedFacilityIds() {
    const selected = new Set();
    const venue = this.element.querySelector('select[name="facility_id"]');
    if (venue && venue.value) selected.add(venue.value);
    this.element
      .querySelectorAll(
        'input[type="checkbox"][name$="_ids[]"]:checked:not([name="accepted_terms_facility_ids[]"])',
      )
      .forEach((cb) => selected.add(cb.value));
    return selected;
  }

  // GR-1970 — show the media block (photo / floor plan / location) for each
  // selected facility, hide the rest. Works for the venue <select> and for the
  // equipment / F&B / service checkboxes. Delegated from the wizard-step
  // `change` action alongside syncTerms.
  syncMedia() {
    const selected = this.selectedFacilityIds();
    this.element.querySelectorAll("[data-facility-media-id]").forEach((block) => {
      block.classList.toggle("d-none", !selected.has(block.dataset.facilityMediaId));
    });
  }

  // GR-1967 — show the T&C acceptance block for each selected terms-required
  // facility, hide the rest. Delegated from a `change` action on the
  // wizard-step so it covers every selection control without per-control
  // wiring.
  syncTerms() {
    const selected = this.selectedFacilityIds();
    this.element.querySelectorAll("[data-facility-terms-id]").forEach((block) => {
      block.classList.toggle("d-none", !selected.has(block.dataset.facilityTermsId));
    });
  }

  // =========================================================================
  // Venue Methods
  // =========================================================================

  syncVenueState() {
    if (!this.hasVenueSameForAllTarget) return;

    const sameForAll = this.venueSameForAllTarget.checked;
    if (this.hasPerDayVenuesBoxTarget) {
      this.setBoxState(this.perDayVenuesBoxTarget, sameForAll);
    }

    if (sameForAll) {
      this.syncVenueToAllDays();
    }
  }

  toggleVenueSameForAll(event) {
    const sameForAll = event.target.checked;
    if (this.hasPerDayVenuesBoxTarget) {
      this.setBoxState(this.perDayVenuesBoxTarget, sameForAll);
    }
    this.updateHiddenField("venue_applies_to_all_days_hidden", sameForAll);

    if (sameForAll) {
      this.syncVenueToAllDays();
    }
  }

  syncVenueToAllDays() {
    if (!this.hasVenueSameForAllTarget || !this.venueSameForAllTarget.checked) {
      return;
    }

    const venueId = this.hasPrimaryVenueTarget
      ? this.primaryVenueTarget.value
      : null;
    const layout = this.hasPrimaryLayoutTarget
      ? this.primaryLayoutTarget.value
      : null;

    this.dayVenueTargets.forEach((select) => {
      if (venueId !== null) select.value = venueId;
    });

    this.dayLayoutTargets.forEach((select) => {
      // GR-3128: the synced venue's own layout options must land first, or
      // setting the layout value silently no-ops on a mismatched list. A
      // primary layout the list lacks (legacy or since-removed value) is
      // appended so the copy never silently drops it.
      if (venueId) this.repopulateLayoutSelect(select, venueId);
      if (layout) {
        if (!Array.from(select.options).some((o) => o.value === layout)) {
          const label = this.hasPrimaryLayoutTarget
            ? this.primaryLayoutTarget.selectedOptions[0]?.textContent
            : null;
          this.appendLayoutOption(select, layout, label || layout);
        }
        select.value = layout;
      } else if (layout !== null) {
        select.value = "";
      }
    });
  }

  // GR-3128 QA bounce — the server may render layout options before any
  // venue is booked, and back-navigation can restore a venue selection the
  // options no longer match. Rebuild every layout select from its venue
  // select's current value; the same-venue rebuild path keeps the current
  // choice (appending it if unoffered) so nothing is silently dropped.
  syncLayoutSelects() {
    if (
      this.hasPrimaryVenueTarget &&
      this.hasPrimaryLayoutTarget &&
      this.primaryVenueTarget.value
    ) {
      this.repopulateLayoutSelect(
        this.primaryLayoutTarget,
        this.primaryVenueTarget.value,
      );
    }
    this.dayVenueTargets.forEach((venueSelect) => {
      const layoutSelect = this.dayLayoutTargets.find(
        (select) => select.dataset.dayId === venueSelect.dataset.dayId,
      );
      if (layoutSelect && venueSelect.value) {
        this.repopulateLayoutSelect(layoutSelect, venueSelect.value);
      }
    });
  }

  onPrimaryVenueChange() {
    // GR-3128: swap the layout options to the newly-picked venue's tiers and
    // apply its default when the previous choice is no longer offered.
    if (this.hasPrimaryVenueTarget && this.hasPrimaryLayoutTarget) {
      this.repopulateLayoutSelect(
        this.primaryLayoutTarget,
        this.primaryVenueTarget.value,
        { applyDefault: true },
      );
    }
    if (this.hasVenueSameForAllTarget && this.venueSameForAllTarget.checked) {
      this.syncVenueToAllDays();
    }
  }

  // GR-3128 — a per-day venue change swaps that row's layout options.
  onDayVenueChange(event) {
    const dayId = event.target.dataset.dayId;
    const layoutSelect = this.dayLayoutTargets.find(
      (select) => select.dataset.dayId === dayId,
    );
    if (layoutSelect) {
      this.repopulateLayoutSelect(layoutSelect, event.target.value, {
        applyDefault: true,
      });
    }
  }

  // GR-3128 — rebuild a layout <select> from the given venue's option set,
  // keeping the current choice when the new venue still offers it, else the
  // venue's default (when asked to), else "Default layout".
  repopulateLayoutSelect(select, venueId, { applyDefault = false } = {}) {
    if (!this.hasLayoutOptionsValue) return;
    const config = this.layoutOptionsValue[venueId];
    if (!config) return;

    const current = select.value;
    const currentLabel = select.selectedOptions[0]?.textContent;
    select.innerHTML = "";
    config.options.forEach(([label, value]) => {
      const option = document.createElement("option");
      option.value = value;
      option.textContent = label;
      select.appendChild(option);
    });

    const offered = config.options.map(([, value]) => value);
    // Review (!4204): only assign a default the options actually contain —
    // the server guards its side, but an unoffered value here would leave
    // selectedIndex at -1 and drop the field from the form submission.
    const fallback =
      config.default && offered.includes(config.default) ? config.default : "";
    if (offered.includes(current)) {
      select.value = current;
    } else if (applyDefault) {
      select.value = fallback;
    } else if (current) {
      // Same-venue rebuild (connect, sync): a persisted value the venue no
      // longer lists stays selectable instead of being silently dropped.
      this.appendLayoutOption(select, current, currentLabel || current);
      select.value = current;
    } else {
      // A persisted blank on a defaulted venue means "the default" — there
      // is no blank option to land on there.
      select.value = fallback;
    }
  }

  appendLayoutOption(select, value, label) {
    const option = document.createElement("option");
    option.value = value;
    option.textContent = label;
    select.appendChild(option);
  }

  onPrimaryLayoutChange() {
    if (this.hasVenueSameForAllTarget && this.venueSameForAllTarget.checked) {
      this.syncVenueToAllDays();
    }
  }

  // =========================================================================
  // Equipment Methods
  // =========================================================================

  syncEquipmentState() {
    if (!this.hasEquipmentSameForAllTarget) return;

    const sameForAll = this.equipmentSameForAllTarget.checked;
    if (this.hasPerDayEquipmentBoxTarget) {
      this.setBoxState(this.perDayEquipmentBoxTarget, sameForAll);
    }

    // Sync column visibility based on primary checkboxes
    this.syncEquipmentColumnVisibility();

    if (sameForAll) {
      this.syncEquipmentToAllDays();
    }
  }

  toggleEquipmentSameForAll(event) {
    const sameForAll = event.target.checked;
    if (this.hasPerDayEquipmentBoxTarget) {
      this.setBoxState(this.perDayEquipmentBoxTarget, sameForAll);
    }
    this.updateHiddenField("equipment_applies_to_all_days_hidden", sameForAll);

    if (sameForAll) {
      this.syncEquipmentToAllDays();
    }
    // The same selection costs a different amount either side of this toggle:
    // shared rows bill once per day, per-day rows bill once.
    this.recalculateEstimate();
  }

  /**
   * Show/hide equipment table columns based on which items are selected in primary
   */
  syncEquipmentColumnVisibility() {
    const selectedIds = new Set();
    this.primaryEquipmentCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        selectedIds.add(checkbox.dataset.equipmentId);
      }
    });

    // Toggle column headers
    this.equipmentColumnTargets.forEach((th) => {
      const equipmentId = th.dataset.equipmentId;
      th.classList.toggle("d-none", !selectedIds.has(equipmentId));
    });

    // Toggle cells
    this.equipmentCellTargets.forEach((td) => {
      const equipmentId = td.dataset.equipmentId;
      td.classList.toggle("d-none", !selectedIds.has(equipmentId));
    });
  }

  /**
   * Sync primary equipment selections to all days in the table
   */
  syncEquipmentToAllDays() {
    if (
      !this.hasEquipmentSameForAllTarget ||
      !this.equipmentSameForAllTarget.checked
    ) {
      return;
    }

    // Get primary selections
    const primaryState = {};
    this.primaryEquipmentCheckboxTargets.forEach((checkbox) => {
      const equipmentId = checkbox.dataset.equipmentId;
      primaryState[equipmentId] = {
        checked: checkbox.checked,
        quantity: this.getPrimaryEquipmentQuantity(equipmentId),
      };
    });

    // Apply to all cells in the table
    this.equipmentCellTargets.forEach((cell) => {
      const equipmentId = cell.dataset.equipmentId;
      const state = primaryState[equipmentId];
      if (!state) return;

      const checkbox = cell.querySelector('input[type="checkbox"]');
      const quantityInput = cell.querySelector('input[type="number"]');

      if (checkbox) {
        checkbox.checked = state.checked;
      }
      if (quantityInput) {
        quantityInput.value = state.quantity;
        quantityInput.disabled = !state.checked;
      }
    });
  }

  getPrimaryEquipmentQuantity(equipmentId) {
    const input = this.primaryEquipmentQuantityTargets.find(
      (el) => el.dataset.equipmentId === equipmentId
    );
    return input ? input.value : 1;
  }

  /**
   * Called when a primary equipment checkbox changes
   */
  onPrimaryEquipmentChange(event) {
    // GR-2969: enable/disable the row's own Qty input. On single-day
    // requests (and the services step, which reuses this handler) this is
    // the only quantity control — a disabled input never submits, so the
    // server silently saved quantity 1 for everything.
    const changedId = event.target.dataset.equipmentId;
    const quantityInput = this.primaryEquipmentQuantityTargets.find(
      (el) => el.dataset.equipmentId === changedId
    );
    if (quantityInput) {
      quantityInput.disabled = !event.target.checked;
    }

    // Update column visibility
    this.syncEquipmentColumnVisibility();

    // If same-for-all is checked, sync to all days
    if (
      this.hasEquipmentSameForAllTarget &&
      this.equipmentSameForAllTarget.checked
    ) {
      this.syncEquipmentToAllDays();
    }

    this.recalculateEstimate();
  }

  onPrimaryEquipmentQuantityChange() {
    if (
      this.hasEquipmentSameForAllTarget &&
      this.equipmentSameForAllTarget.checked
    ) {
      this.syncEquipmentToAllDays();
    }

    this.recalculateEstimate();
  }

  /**
   * GR-3546: the row a per-day checkbox lives in.
   *
   * These rows were a table once and both handlers looked the row up with
   * closest("td"). Steps 4, 5 and 6 now render them with Primer's
   * BorderBox#with_row (li.Box-row), so the lookup returned null and the
   * per-row toggle was dead: ticking or unticking a day never changed its
   * Qty / Headcount input in either direction. The typed number still
   * posted, because setBoxState had already enabled every input in the box.
   * The reason the toggle matters is that a disabled input is not submitted,
   * and EventRequests::BookingReconciler then falls back to its default
   * (quantity 1, or day.capacity || expected_attendance || 10 for catering).
   * That fallback is the risk this lookup guards against, not a fault that
   * was observed.
   *
   * Same resolution order as the wizard controller's
   * toggleConsumableHeadcount. No td fallback: closest(".d-flex") would
   * match before it ever could.
   */
  perDayRowFor(checkbox) {
    return (
      checkbox.closest('[class*="Box-row"]') || checkbox.closest(".d-flex")
    );
  }

  /**
   * GR-3546: setBoxState(box, false) enables every input in a per-day box,
   * which overrides the server-rendered disabled state on rows whose day is
   * not ticked. Put each row's number input back in step with its checkbox,
   * the same rule the per-row change handlers apply.
   */
  syncPerDayRowInputs(box) {
    box.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      const numberInput = this.perDayRowFor(checkbox)?.querySelector(
        'input[type="number"]',
      );
      if (numberInput) {
        numberInput.disabled = !checkbox.checked;
      }
    });
  }

  /**
   * Called when a per-day equipment checkbox changes - enable/disable quantity input
   */
  onDayEquipmentCheckboxChange(event) {
    const checkbox = event.target;
    const row = this.perDayRowFor(checkbox);
    const quantityInput = row?.querySelector('input[type="number"]');

    if (quantityInput) {
      quantityInput.disabled = !checkbox.checked;
    }

    this.recalculateEstimate();
  }

  // =========================================================================
  // F&B Methods
  // =========================================================================

  syncFbState() {
    if (!this.hasFbSameForAllTarget) return;

    const sameForAll = this.fbSameForAllTarget.checked;
    if (this.hasPerDayFbBoxTarget) {
      this.setBoxState(this.perDayFbBoxTarget, sameForAll);
    }

    // Sync column visibility based on primary checkboxes
    this.syncFbColumnVisibility();

    if (sameForAll) {
      this.syncFbToAllDays();
    }
  }

  toggleFbSameForAll(event) {
    const sameForAll = event.target.checked;
    if (this.hasPerDayFbBoxTarget) {
      this.setBoxState(this.perDayFbBoxTarget, sameForAll);
    }
    this.updateHiddenField("fb_applies_to_all_days_hidden", sameForAll);

    if (sameForAll) {
      this.syncFbToAllDays();
    }
  }

  /**
   * Show/hide F&B table columns based on which items are selected in primary
   */
  syncFbColumnVisibility() {
    const selectedIds = new Set();
    this.primaryFbCheckboxTargets.forEach((checkbox) => {
      if (checkbox.checked) {
        selectedIds.add(checkbox.dataset.consumableId);
      }
    });

    // Toggle column headers
    this.fbColumnTargets.forEach((th) => {
      const consumableId = th.dataset.consumableId;
      th.classList.toggle("d-none", !selectedIds.has(consumableId));
    });

    // Toggle cells
    this.fbCellTargets.forEach((td) => {
      const consumableId = td.dataset.consumableId;
      td.classList.toggle("d-none", !selectedIds.has(consumableId));
    });
  }

  /**
   * Sync primary F&B selections to all days in the table
   */
  syncFbToAllDays() {
    if (!this.hasFbSameForAllTarget || !this.fbSameForAllTarget.checked) {
      return;
    }

    // Get primary selections
    const primaryState = {};
    this.primaryFbCheckboxTargets.forEach((checkbox) => {
      const consumableId = checkbox.dataset.consumableId;
      primaryState[consumableId] = {
        checked: checkbox.checked,
        headcount: this.getPrimaryFbHeadcount(consumableId),
      };
    });

    // Apply to all cells in the table
    this.fbCellTargets.forEach((cell) => {
      const consumableId = cell.dataset.consumableId;
      const state = primaryState[consumableId];
      if (!state) return;

      const checkbox = cell.querySelector('input[type="checkbox"]');
      const headcountInput = cell.querySelector('input[type="number"]');

      if (checkbox) {
        checkbox.checked = state.checked;
      }
      if (headcountInput) {
        headcountInput.value = state.headcount;
        headcountInput.disabled = !state.checked;
      }
    });
  }

  getPrimaryFbHeadcount(consumableId) {
    const input = this.primaryFbHeadcountTargets.find(
      (el) => el.dataset.consumableId === consumableId
    );
    return input
      ? input.value
      : this.element.dataset.defaultHeadcount || 50;
  }

  /**
   * Called when a primary F&B checkbox changes
   */
  onPrimaryFbChange(event) {
    // Update column visibility
    this.syncFbColumnVisibility();

    // If same-for-all is checked, sync to all days
    if (this.hasFbSameForAllTarget && this.fbSameForAllTarget.checked) {
      this.syncFbToAllDays();
    }
  }

  onPrimaryFbHeadcountChange() {
    if (this.hasFbSameForAllTarget && this.fbSameForAllTarget.checked) {
      this.syncFbToAllDays();
    }
  }

  /**
   * Called when a per-day F&B checkbox changes - enable/disable headcount input
   */
  onDayFbCheckboxChange(event) {
    const checkbox = event.target;
    const row = this.perDayRowFor(checkbox);
    const headcountInput = row?.querySelector('input[type="number"]');

    if (headcountInput) {
      headcountInput.disabled = !checkbox.checked;
    }
  }

  // =========================================================================
  // Running Estimate (GR-3294)
  // =========================================================================

  /**
   * Recompute the step's running estimate from the rows.
   *
   * The arithmetic is EventRequests::InvoiceGenerator's, because a total that
   * disagrees with the estimate the requester meets on the next screen is
   * worse than no total: rate x quantity, multiplied by the day count when
   * the booking applies to every day. Which set of rows is authoritative
   * follows the same rule the server uses on submit — the shared rows while
   * "same for all days" is checked, the per-day rows otherwise.
   *
   * No-ops on steps that render no estimate (step 5 today).
   */
  recalculateEstimate() {
    if (!this.hasEstimateTotalTarget) return;

    const sameForAll =
      !this.hasEquipmentSameForAllTarget ||
      this.equipmentSameForAllTarget.checked;
    const days =
      this.hasEstimateDaysValue && this.estimateDaysValue > 0
        ? this.estimateDaysValue
        : 1;

    let cents = 0;
    let withheld = false;

    const add = (facilityId, quantity, multiplier) => {
      if (this.rateIsWithheld(facilityId)) {
        withheld = true;
        return;
      }
      cents += this.rateCentsFor(facilityId) * quantity * multiplier;
    };

    if (sameForAll) {
      this.primaryEquipmentCheckboxTargets.forEach((checkbox) => {
        if (!checkbox.checked) return;
        const id = checkbox.dataset.equipmentId;
        add(id, this.quantityValue(this.getPrimaryEquipmentQuantity(id)), days);
      });
    } else {
      this.equipmentCellTargets.forEach((cell) => {
        const checkbox = cell.querySelector('input[type="checkbox"]');
        if (!checkbox?.checked) return;
        const input = cell.querySelector('input[type="number"]');
        add(cell.dataset.equipmentId, this.quantityValue(input?.value), 1);
      });
    }

    this.estimateTotalTarget.textContent = this.formatCents(cents);
    if (this.hasEstimateNoteTarget) {
      this.estimateNoteTarget.classList.toggle("d-none", !withheld);
    }
  }

  // The rate lives on the row's rate line rather than on the checkbox, so
  // there is one copy of it in the DOM and a withheld rate is never written
  // out as a number at all (GR-1969) — only as the marker below.
  rateCentsFor(facilityId) {
    const line = this.element.querySelector(
      `[data-facility-rate="${facilityId}"]`,
    );
    return Number(line?.dataset.rateCents || 0);
  }

  rateIsWithheld(facilityId) {
    return !!this.element.querySelector(
      `[data-facility-rate-hidden="${facilityId}"]`,
    );
  }

  // A blank or unparseable quantity box is 1, matching the server's
  // `booking.quantity || 1` rather than silently zeroing the row.
  quantityValue(raw) {
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  formatCents(cents) {
    const currency = this.hasEstimateCurrencyValue
      ? this.estimateCurrencyValue
      : "USD";
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(cents / 100);
  }

  // =========================================================================
  // Helper Methods
  // =========================================================================

  setBoxState(box, sameForAll) {
    if (!box) return;
    box.style.opacity = sameForAll ? "0.6" : "1";
    box.setAttribute("aria-disabled", sameForAll ? "true" : "false");

    // Disable/enable all form inputs for proper accessibility
    box
      .querySelectorAll("input, select, textarea, button")
      .forEach((el) => (el.disabled = sameForAll));

    if (!sameForAll) {
      this.syncPerDayRowInputs(box);
    }
  }

  updateHiddenField(fieldId, sameForAll) {
    const hiddenField = document.getElementById(fieldId);
    if (hiddenField) {
      hiddenField.value = sameForAll ? "true" : "false";
    }
  }
}
